import { isDefined } from "@/utils/function-utils";
import { mappedCompare } from "@/utils/sort";

export type Entry<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T];

export function objectFilter<T extends object>(obj: T, predicate: ([a, b]: Entry<T>) => boolean) {
  return Object.fromEntries((Object.entries(obj) as Entry<T>[]).filter(predicate)) as Partial<T>;
}

export function omap<IN, OUT>(
  value: IN | undefined | null,
  fn: (input: IN) => OUT,
): OUT | undefined {
  return value != null ? fn(value) : undefined;
}

export function omapAlt<IN, OUT>(
  value: IN | undefined | null,
  fn: (input: IN) => OUT,
  alt: OUT,
): OUT {
  return value != null ? fn(value) : alt;
}

/**
 * It's faster than lodash compact and only filters out nullish values. Lodash compact filters
 * out all _falsy_ values.
 */
export function fastCompact<T>(input: Array<T | undefined | null>): Array<T> {
  return input.filter(isDefined);
}

export function objectToKeyValues<
  T extends Record<string, unknown> = Record<string, string | number>,
>(obj: T, sortKeys: boolean = true): Array<{ key: keyof T; value: T[keyof T] }> {
  const keyValues = Object.entries(obj).map(([key, value]) => ({
    key: key as keyof T,
    value: value as T[keyof T],
  }));

  if (sortKeys) {
    return keyValues.sort(mappedCompare((keyValue) => keyValue.key as string));
  }

  return keyValues;
}

export function keyValuesToObject<T extends object>(
  keyValues: Array<{ key: keyof T; value: T[keyof T] }>,
): T {
  return Object.fromEntries(keyValues.map(({ key, value }) => [key, value])) as T;
}

export const isEmptyObject = (maybeObj: unknown): maybeObj is Record<string, never> => {
  return typeof maybeObj === "object" && isDefined(maybeObj) && Object.keys(maybeObj).length === 0;
};

export const isNonEmptyObject = <T extends object>(obj: T | null | undefined): obj is T => {
  return typeof obj === "object" && isDefined(obj) && Object.keys(obj).length > 0;
};

export const fastDeepCopy = <T extends object>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

export const recursivelyOmitEmptyStringValues = <T>(obj: T): T => {
  if (typeof obj !== "object" || obj == null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(recursivelyOmitEmptyStringValues) as T;
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_key, value]) => typeof value !== "string" || value.trim().length > 0)
      .map(([key, value]) => {
        return [key, recursivelyOmitEmptyStringValues(value)];
      }),
  ) as T;
};

export const omitEmptyKey = (
  obj: Record<string, string> | null | undefined,
): Record<string, string> | null | undefined => {
  if (obj == null) {
    return obj;
  }
  const newObj = { ...obj };
  if ("" in newObj) {
    delete newObj[""];
  }

  return newObj;
};
